import { Capacitor } from '@capacitor/core';
import type { CustomerInfo } from '@revenuecat/purchases-capacitor';
import type { PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { listMonetizationEvents, type MonetizationEvent } from './monetizationEvents';

export const ENTITLEMENT_CACHE_KEY = 'billing_entitlement_v1';

export type EntitlementTier = 'free' | 'ad_free' | 'pro';

export type EntitlementState = {
  tier: EntitlementTier;
  source: 'play' | 'cache' | 'none';
  updatedAtMs: number;
};

export type MonetizationPlan = {
  kind: 'ad_free' | 'pro';
  productId: string;
  title: string;
  priceLabel: string;
};

export type PurchasePlanStatus = 'success' | 'canceled' | 'failed' | 'unavailable';

export type PurchasePlanResult = {
  status: PurchasePlanStatus;
  state: EntitlementState;
};

export type BillingDiagnosticCheck = {
  key:
    | 'native_android'
    | 'revenuecat_key'
    | 'admob_app_id'
    | 'admob_interstitial_id'
    | 'product_ids'
    | 'offering_packages'
    | 'active_customer_info';
  ok: boolean;
  detail: string;
};

export type BillingDiagnostics = {
  checkedAtMs: number;
  platform: string;
  isNativeAndroid: boolean;
  revenueCatConfigured: boolean;
  revenueCatApiKeyPresent: boolean;
  admobAppIdPresent: boolean;
  admobInterstitialIdPresent: boolean;
  admobUsingTestIdentifiers: boolean;
  adFreeProductId: string;
  proProductIds: string[];
  adFreeEntitlementId: string;
  proEntitlementId: string;
  appUserId: string | null;
  currentOfferingId: string | null;
  availablePackageCount: number;
  availablePackages: MonetizationPlan[];
  activeEntitlements: string[];
  activeSubscriptions: string[];
  managementUrl: string | null;
  recentEvents: MonetizationEvent[];
  checks: BillingDiagnosticCheck[];
  error: string | null;
};

export type MonetizationConfigSnapshot = {
  revenueCatApiKeyPresent: boolean;
  admobAppIdPresent: boolean;
  admobInterstitialIdPresent: boolean;
  admobUsingTestIdentifiers: boolean;
  adFreeProductId: string;
  proProductIds: string[];
};

type RawPurchase = Record<string, unknown>;

type BillingPlugin = {
  initialize?: (...args: unknown[]) => Promise<unknown>;
  init?: (...args: unknown[]) => Promise<unknown>;
  connect?: (...args: unknown[]) => Promise<unknown>;
  getPurchases?: (...args: unknown[]) => Promise<unknown>;
  listPurchases?: (...args: unknown[]) => Promise<unknown>;
  getPurchaseHistory?: (...args: unknown[]) => Promise<unknown>;
  restorePurchases?: (...args: unknown[]) => Promise<unknown>;
};

let revenueCatConfigured = false;
let revenueCatConfiguredKey = '';

export function billingConfigured(): boolean {
  return hasRevenueCatConfig() || adFreeProductId().length > 0 || proProductIds().length > 0;
}

export function getMonetizationConfigSnapshot(): MonetizationConfigSnapshot {
  return {
    revenueCatApiKeyPresent: revenueCatApiKey().length > 0,
    admobAppIdPresent: admobAppId().length > 0,
    admobInterstitialIdPresent: admobInterstitialId().length > 0,
    admobUsingTestIdentifiers: isUsingTestAdmobIds(),
    adFreeProductId: adFreeProductId(),
    proProductIds: proProductIds()
  };
}

export function getCachedEntitlement(): EntitlementState {
  const fallback: EntitlementState = { tier: 'free', source: 'none', updatedAtMs: 0 };
  const raw = localStorage.getItem(ENTITLEMENT_CACHE_KEY);
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as Partial<EntitlementState>;
    if (parsed.tier !== 'free' && parsed.tier !== 'ad_free' && parsed.tier !== 'pro') return fallback;
    const updatedAtMs = Number(parsed.updatedAtMs ?? 0);
    if (!Number.isFinite(updatedAtMs) || updatedAtMs < 0) return fallback;
    return {
      tier: parsed.tier,
      source: 'cache',
      updatedAtMs
    };
  } catch {
    return fallback;
  }
}

export async function refreshEntitlementFromStore(): Promise<EntitlementState> {
  const fallback = getCachedEntitlement();
  if (!billingConfigured()) return fallback;
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return fallback;

  if (hasRevenueCatConfig()) {
    return refreshFromRevenueCat(fallback);
  }

  const plugin = resolveBillingPlugin();
  if (!plugin) return fallback;

  try {
    await initializePlugin(plugin);
    const purchases = await readPurchases(plugin);
    const tier = deriveTier(purchases);
    const next: EntitlementState = {
      tier,
      source: 'play',
      updatedAtMs: Date.now()
    };
    localStorage.setItem(ENTITLEMENT_CACHE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return fallback;
  }
}

export async function restoreEntitlementsFromStore(): Promise<EntitlementState> {
  const fallback = getCachedEntitlement();
  if (!billingConfigured()) return fallback;
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return fallback;

  if (hasRevenueCatConfig()) {
    return restoreFromRevenueCat(fallback);
  }

  const plugin = resolveBillingPlugin();
  if (!plugin) return fallback;

  try {
    await initializePlugin(plugin);
    if (typeof plugin.restorePurchases === 'function') {
      await plugin.restorePurchases();
    }
    return refreshEntitlementFromStore();
  } catch {
    return fallback;
  }
}

export async function listAvailablePlans(): Promise<MonetizationPlan[]> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return [];
  if (!hasRevenueCatConfig()) return [];
  try {
    const purchases = await getPurchasesSdk();
    if (!purchases) return [];
    await ensureRevenueCatConfigured(purchases);
    const offerings = await purchases.getOfferings();
    const current = offerings.current;
    if (!current) return [];
    const items = current.availablePackages
      .map((pkg) => mapPackageToPlan(pkg))
      .filter((item): item is MonetizationPlan => item !== null);
    return uniquePlans(items);
  } catch {
    return [];
  }
}

export async function purchasePlan(productId: string): Promise<PurchasePlanResult> {
  const fallback = getCachedEntitlement();
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    return { status: 'unavailable', state: fallback };
  }
  if (!hasRevenueCatConfig()) return { status: 'unavailable', state: fallback };
  try {
    const purchases = await getPurchasesSdk();
    if (!purchases) return { status: 'unavailable', state: fallback };
    await ensureRevenueCatConfigured(purchases);
    const offerings = await purchases.getOfferings();
    const match = findPackageByProductId(offerings.current?.availablePackages ?? [], productId);
    if (!match) return { status: 'unavailable', state: fallback };
    const result = await purchases.purchasePackage({ aPackage: match });
    return { status: 'success', state: cacheFromCustomerInfo(result.customerInfo) };
  } catch (error) {
    return { status: classifyPurchaseError(error), state: fallback };
  }
}

export async function getSubscriptionManagementUrl(): Promise<string | null> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return null;
  if (!hasRevenueCatConfig()) return null;
  try {
    const purchases = await getPurchasesSdk();
    if (!purchases) return null;
    await ensureRevenueCatConfigured(purchases);
    const { customerInfo } = await purchases.getCustomerInfo();
    return readString(customerInfo.managementURL) ?? null;
  } catch {
    return null;
  }
}

export async function getBillingDiagnostics(): Promise<BillingDiagnostics> {
  const config = getMonetizationConfigSnapshot();
  const diagnostics: BillingDiagnostics = {
    checkedAtMs: Date.now(),
    platform: Capacitor.getPlatform(),
    isNativeAndroid: Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android',
    revenueCatConfigured: hasRevenueCatConfig(),
    revenueCatApiKeyPresent: config.revenueCatApiKeyPresent,
    admobAppIdPresent: config.admobAppIdPresent,
    admobInterstitialIdPresent: config.admobInterstitialIdPresent,
    admobUsingTestIdentifiers: config.admobUsingTestIdentifiers,
    adFreeProductId: config.adFreeProductId,
    proProductIds: config.proProductIds,
    adFreeEntitlementId: revenueCatAdFreeEntitlementId(),
    proEntitlementId: revenueCatProEntitlementId(),
    appUserId: null,
    currentOfferingId: null,
    availablePackageCount: 0,
    availablePackages: [],
    activeEntitlements: [],
    activeSubscriptions: [],
    managementUrl: null,
    recentEvents: listMonetizationEvents(),
    checks: [],
    error: null
  };

  diagnostics.checks.push({
    key: 'native_android',
    ok: diagnostics.isNativeAndroid,
    detail: diagnostics.isNativeAndroid ? 'Running on native Android build.' : `Current platform: ${diagnostics.platform}.`
  });
  diagnostics.checks.push({
    key: 'revenuecat_key',
    ok: diagnostics.revenueCatApiKeyPresent,
    detail: diagnostics.revenueCatApiKeyPresent ? 'RevenueCat API key is present.' : 'RevenueCat API key is missing.'
  });
  diagnostics.checks.push({
    key: 'admob_app_id',
    ok: diagnostics.admobAppIdPresent,
    detail: diagnostics.admobAppIdPresent ? 'AdMob Android app ID is present.' : 'AdMob Android app ID is missing.'
  });
  diagnostics.checks.push({
    key: 'admob_interstitial_id',
    ok: diagnostics.admobInterstitialIdPresent,
    detail: diagnostics.admobInterstitialIdPresent ? 'AdMob interstitial unit ID is present.' : 'AdMob interstitial unit ID is missing.'
  });
  diagnostics.checks.push({
    key: 'product_ids',
    ok: diagnostics.adFreeProductId.length > 0 || diagnostics.proProductIds.length > 0 || diagnostics.revenueCatConfigured,
    detail:
      diagnostics.adFreeProductId.length > 0 || diagnostics.proProductIds.length > 0 || diagnostics.revenueCatConfigured
        ? 'Billing product identifiers or RevenueCat configuration are present.'
        : 'No billing product identifiers are configured.'
  });
  if (diagnostics.admobUsingTestIdentifiers) {
    diagnostics.checks.push({
      key: 'admob_app_id',
      ok: false,
      detail: 'AdMob is using Google test identifiers. Replace them before production release.'
    });
  }

  if (!diagnostics.isNativeAndroid || !diagnostics.revenueCatConfigured) {
    diagnostics.checks.push({
      key: 'offering_packages',
      ok: false,
      detail: 'Offerings unavailable until native Android + RevenueCat config are active.'
    });
    diagnostics.checks.push({
      key: 'active_customer_info',
      ok: false,
      detail: 'Customer info unavailable until native Android + RevenueCat config are active.'
    });
    return diagnostics;
  }

  try {
    const purchases = await getPurchasesSdk();
    if (!purchases) {
      diagnostics.error = 'RevenueCat SDK plugin not available in this build.';
      diagnostics.checks.push({
        key: 'offering_packages',
        ok: false,
        detail: 'RevenueCat SDK plugin unavailable.'
      });
      diagnostics.checks.push({
        key: 'active_customer_info',
        ok: false,
        detail: 'RevenueCat SDK plugin unavailable.'
      });
      return diagnostics;
    }

    await ensureRevenueCatConfigured(purchases);
    const [offerings, customer] = await Promise.all([purchases.getOfferings(), purchases.getCustomerInfo()]);
    const current = offerings.current;

    diagnostics.currentOfferingId = current?.identifier ?? null;
    diagnostics.availablePackages = uniquePlans(
      (current?.availablePackages ?? []).map((pkg) => mapPackageToPlan(pkg)).filter((item): item is MonetizationPlan => item !== null)
    );
    diagnostics.availablePackageCount = diagnostics.availablePackages.length;
    diagnostics.appUserId = readString(customer.customerInfo.originalAppUserId);
    diagnostics.managementUrl = readString(customer.customerInfo.managementURL);
    diagnostics.activeEntitlements = Object.keys(customer.customerInfo.entitlements?.active ?? {});
    diagnostics.activeSubscriptions = [...(customer.customerInfo.activeSubscriptions ?? [])];

    diagnostics.checks.push({
      key: 'offering_packages',
      ok: diagnostics.availablePackageCount > 0,
      detail:
        diagnostics.availablePackageCount > 0
          ? `${diagnostics.availablePackageCount} package(s) found in current offering.`
          : 'No packages in current offering.'
    });
    diagnostics.checks.push({
      key: 'active_customer_info',
      ok: diagnostics.appUserId !== null,
      detail: diagnostics.appUserId ? `App user ID: ${diagnostics.appUserId}` : 'Customer info loaded, but app user ID is empty.'
    });
    return diagnostics;
  } catch (error) {
    diagnostics.error = errorMessage(error);
    diagnostics.checks.push({
      key: 'offering_packages',
      ok: false,
      detail: diagnostics.error
    });
    diagnostics.checks.push({
      key: 'active_customer_info',
      ok: false,
      detail: diagnostics.error
    });
    return diagnostics;
  }
}

function resolveBillingPlugin(): BillingPlugin | null {
  const plugins = (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor?.Plugins;
  if (!plugins) return null;
  const candidate = (plugins.Billing ?? plugins.InAppPurchase ?? plugins.Purchases) as BillingPlugin | undefined;
  return candidate ?? null;
}

async function initializePlugin(plugin: BillingPlugin): Promise<void> {
  if (typeof plugin.initialize === 'function') {
    await plugin.initialize();
    return;
  }
  if (typeof plugin.init === 'function') {
    await plugin.init();
    return;
  }
  if (typeof plugin.connect === 'function') {
    await plugin.connect();
  }
}

async function readPurchases(plugin: BillingPlugin): Promise<RawPurchase[]> {
  if (typeof plugin.getPurchases === 'function') {
    return normalizePurchases(await plugin.getPurchases());
  }
  if (typeof plugin.listPurchases === 'function') {
    return normalizePurchases(await plugin.listPurchases());
  }
  if (typeof plugin.getPurchaseHistory === 'function') {
    return normalizePurchases(await plugin.getPurchaseHistory());
  }
  return [];
}

function normalizePurchases(payload: unknown): RawPurchase[] {
  if (Array.isArray(payload)) return payload.filter(isRecord);
  if (!isRecord(payload)) return [];
  const withCollection = payload as { purchases?: unknown; items?: unknown; purchaseHistory?: unknown };
  if (Array.isArray(withCollection.purchases)) return withCollection.purchases.filter(isRecord);
  if (Array.isArray(withCollection.items)) return withCollection.items.filter(isRecord);
  if (Array.isArray(withCollection.purchaseHistory)) return withCollection.purchaseHistory.filter(isRecord);
  return [];
}

function deriveTier(purchases: RawPurchase[]): EntitlementTier {
  const activeProductIds = new Set<string>();
  for (const purchase of purchases) {
    if (!isActivePurchase(purchase)) continue;
    const id = purchaseProductId(purchase);
    if (!id) continue;
    activeProductIds.add(id);
  }

  const proIds = proProductIds();
  for (const id of proIds) {
    if (activeProductIds.has(id)) return 'pro';
  }

  const adFree = adFreeProductId();
  if (adFree && activeProductIds.has(adFree)) return 'ad_free';
  return 'free';
}

function isActivePurchase(purchase: RawPurchase): boolean {
  const consumed = purchase.isConsumed;
  if (consumed === true) return false;

  const canceled = purchase.isCanceled;
  if (canceled === true) return false;

  const state = purchase.purchaseState;
  if (typeof state === 'number' && state !== 1) return false;
  if (typeof state === 'string') {
    const upper = state.toUpperCase();
    if (upper !== 'PURCHASED') return false;
  }

  const expiryMsRaw = purchase.expiryTimeMillis ?? purchase.expiresAtMs;
  if (typeof expiryMsRaw === 'number' && Number.isFinite(expiryMsRaw) && expiryMsRaw > 0 && expiryMsRaw < Date.now()) {
    return false;
  }
  if (typeof expiryMsRaw === 'string') {
    const expiryMs = Number(expiryMsRaw);
    if (Number.isFinite(expiryMs) && expiryMs > 0 && expiryMs < Date.now()) return false;
  }

  return true;
}

function purchaseProductId(purchase: RawPurchase): string | null {
  const direct = readString(purchase.productId) ?? readString(purchase.productIdentifier) ?? readString(purchase.sku) ?? readString(purchase.id);
  if (direct) return direct;
  const productIds = purchase.productIds;
  if (Array.isArray(productIds)) {
    const first = productIds.find((item) => typeof item === 'string' && item.trim().length > 0);
    if (typeof first === 'string') return first.trim();
  }
  return null;
}

function adFreeProductId(): string {
  const value = import.meta.env.VITE_PLAY_BILLING_AD_FREE_PRODUCT_ID;
  if (typeof value !== 'string') return '';
  return value.trim();
}

function admobAppId(): string {
  const value = import.meta.env.VITE_ADMOB_ANDROID_APP_ID;
  if (typeof value !== 'string') return '';
  return value.trim();
}

function admobInterstitialId(): string {
  const value = import.meta.env.VITE_ADMOB_ANDROID_INTERSTITIAL_ID;
  if (typeof value !== 'string') return '';
  return value.trim();
}

function isUsingTestAdmobIds(): boolean {
  return admobAppId() === 'ca-app-pub-3940256099942544~3347511713' || admobInterstitialId() === 'ca-app-pub-3940256099942544/1033173712';
}

function proProductIds(): string[] {
  const explicit = import.meta.env.VITE_PLAY_BILLING_PRO_PRODUCT_IDS;
  if (typeof explicit === 'string' && explicit.trim().length > 0) {
    return explicit
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }
  const monthly = import.meta.env.VITE_PLAY_BILLING_PRO_MONTHLY_PRODUCT_ID;
  const yearly = import.meta.env.VITE_PLAY_BILLING_PRO_YEARLY_PRODUCT_ID;
  return [monthly, yearly].filter((item): item is string => typeof item === 'string' && item.trim().length > 0).map((item) => item.trim());
}

function hasRevenueCatConfig(): boolean {
  return revenueCatApiKey().length > 0;
}

function revenueCatApiKey(): string {
  const android = import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY;
  if (typeof android === 'string' && android.trim().length > 0) return android.trim();
  return '';
}

function revenueCatAdFreeEntitlementId(): string {
  const value = import.meta.env.VITE_REVENUECAT_ENTITLEMENT_AD_FREE;
  if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  return 'ad_free';
}

function revenueCatProEntitlementId(): string {
  const value = import.meta.env.VITE_REVENUECAT_ENTITLEMENT_PRO;
  if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  return 'pro';
}

async function refreshFromRevenueCat(fallback: EntitlementState): Promise<EntitlementState> {
  try {
    const purchases = await getPurchasesSdk();
    if (!purchases) return fallback;
    await ensureRevenueCatConfigured(purchases);
    const { customerInfo } = await purchases.getCustomerInfo();
    return cacheFromCustomerInfo(customerInfo);
  } catch {
    return fallback;
  }
}

async function restoreFromRevenueCat(fallback: EntitlementState): Promise<EntitlementState> {
  try {
    const purchases = await getPurchasesSdk();
    if (!purchases) return fallback;
    await ensureRevenueCatConfigured(purchases);
    const { customerInfo } = await purchases.restorePurchases();
    return cacheFromCustomerInfo(customerInfo);
  } catch {
    return fallback;
  }
}

async function getPurchasesSdk(): Promise<(typeof import('@revenuecat/purchases-capacitor'))['Purchases'] | null> {
  try {
    const module = await import('@revenuecat/purchases-capacitor');
    return module.Purchases;
  } catch {
    return null;
  }
}

async function ensureRevenueCatConfigured(purchases: (typeof import('@revenuecat/purchases-capacitor'))['Purchases']): Promise<void> {
  const key = revenueCatApiKey();
  if (!key) return;
  if (revenueCatConfigured && revenueCatConfiguredKey === key) return;
  await purchases.configure({ apiKey: key });
  revenueCatConfigured = true;
  revenueCatConfiguredKey = key;
}

export function classifyPurchaseError(error: unknown): PurchasePlanStatus {
  if (isUserCanceledPurchase(error)) return 'canceled';
  return 'failed';
}

function isUserCanceledPurchase(error: unknown): boolean {
  if (!isRecord(error)) return false;
  if (error.userCancelled === true) return true;

  const code = readString(error.readableErrorCode) ?? readString(error.code) ?? '';
  const message = readString(error.message) ?? '';
  const combined = `${code} ${message}`.toUpperCase();
  return combined.includes('USER_CANCELLED') || combined.includes('PURCHASE_CANCELLED') || combined.includes('CANCELLED');
}

function cacheFromCustomerInfo(customerInfo: CustomerInfo): EntitlementState {
  const tier = deriveTierFromCustomerInfo(customerInfo);
  const next: EntitlementState = {
    tier,
    source: 'play',
    updatedAtMs: Date.now()
  };
  localStorage.setItem(ENTITLEMENT_CACHE_KEY, JSON.stringify(next));
  return next;
}

function deriveTierFromCustomerInfo(customerInfo: CustomerInfo): EntitlementTier {
  const active = customerInfo.entitlements?.active ?? {};
  const proEntitlement = active[revenueCatProEntitlementId()];
  if (proEntitlement?.isActive) return 'pro';
  const adFreeEntitlement = active[revenueCatAdFreeEntitlementId()];
  if (adFreeEntitlement?.isActive) return 'ad_free';

  const activeSubscriptionIds = new Set(customerInfo.activeSubscriptions ?? []);
  for (const id of proProductIds()) {
    if (activeSubscriptionIds.has(id)) return 'pro';
  }
  const adFreeProduct = adFreeProductId();
  if (adFreeProduct && activeSubscriptionIds.has(adFreeProduct)) return 'ad_free';
  return 'free';
}

function mapPackageToPlan(pkg: PurchasesPackage): MonetizationPlan | null {
  const productId = pkg.product.identifier;
  const adFreeId = adFreeProductId();
  const proIds = new Set(proProductIds());
  const inferredFromTitle = (pkg.product.title ?? '').toLowerCase();
  const inferredFromId = productId.toLowerCase();
  const kind: 'ad_free' | 'pro' =
    productId === adFreeId || inferredFromId.includes('ad_free') || inferredFromTitle.includes('ad-free')
      ? 'ad_free'
      : proIds.has(productId)
        ? 'pro'
        : 'pro';
  return {
    kind,
    productId,
    title: pkg.product.title || productId,
    priceLabel: pkg.product.priceString || String(pkg.product.price)
  };
}

function uniquePlans(plans: MonetizationPlan[]): MonetizationPlan[] {
  const seen = new Set<string>();
  const result: MonetizationPlan[] = [];
  for (const plan of plans) {
    if (seen.has(plan.productId)) continue;
    seen.add(plan.productId);
    result.push(plan);
  }
  return result.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'ad_free' ? -1 : 1;
    return a.priceLabel.localeCompare(b.priceLabel);
  });
}

function findPackageByProductId(packages: PurchasesPackage[], productId: string): PurchasesPackage | null {
  return packages.find((pkg) => pkg.product.identifier === productId) ?? null;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) return error.message;
  if (typeof error === 'string' && error.trim().length > 0) return error;
  return 'Unknown billing error.';
}
